import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FolderPlus, Trash2, Edit2, X, Folder } from "lucide-react";
import { QuestionGroup, QuestionGroupNode, FormQuestion } from "@/hooks/useQuestionMappings";
import { toast } from "sonner";
import { countQuestionsInNode, countSubgroupsInNode } from "@/utils/questionGroupHelpers";

interface GroupedQuestionsProps {
  groups: QuestionGroup;
  formQuestions: FormQuestion[];
  onCreateGroup: (path: string[], groupName: string) => void;
  onRenameGroup: (path: string[], newName: string) => void;
  onDeleteGroup: (path: string[]) => void;
  onRemoveQuestion: (path: string[], questionName: string) => void;
}

interface GroupNodeProps {
  groupName: string;
  node: QuestionGroupNode;
  path: string[];
  formQuestions: FormQuestion[];
  onCreateSubgroup: (path: string[], groupName: string) => void;
  onRenameGroup: (path: string[], newName: string) => void;
  onDeleteGroup: (path: string[]) => void;
  onRemoveQuestion: (path: string[], questionName: string) => void;
  level?: number;
}

function GroupNode({
  groupName,
  node,
  path,
  formQuestions,
  onCreateSubgroup,
  onRenameGroup,
  onDeleteGroup,
  onRemoveQuestion,
  level = 0
}: GroupNodeProps) {
  const [editingGroup, setEditingGroup] = useState(false);
  const [editGroupName, setEditGroupName] = useState(groupName);
  const [newSubgroupName, setNewSubgroupName] = useState("");
  const [isSubgroupDialogOpen, setIsSubgroupDialogOpen] = useState(false);

  const currentPath = [...path, groupName];
  const totalQuestions = countQuestionsInNode(node);
  const totalSubgroups = countSubgroupsInNode(node);

  const handleStartRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroup(true);
    setEditGroupName(groupName);
  };

  const handleSaveRename = () => {
    if (!editGroupName.trim()) {
      toast.error("El nombre del grupo no puede estar vacío");
      return;
    }

    try {
      onRenameGroup(currentPath, editGroupName.trim());
      setEditingGroup(false);
      setEditGroupName("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al renombrar");
    }
  };

  const handleCancelRename = () => {
    setEditingGroup(false);
    setEditGroupName(groupName);
  };

  const handleCreateSubgroup = () => {
    if (!newSubgroupName.trim()) {
      toast.error("El nombre del subgrupo no puede estar vacío");
      return;
    }

    try {
      onCreateSubgroup(currentPath, newSubgroupName.trim());
      setNewSubgroupName("");
      setIsSubgroupDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al crear subgrupo");
    }
  };

  const handleDeleteGroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmMessage = totalQuestions > 0 || totalSubgroups > 0
      ? `¿Estás seguro de eliminar "${groupName}"? Se eliminarán ${totalQuestions} pregunta(s) y ${totalSubgroups} subgrupo(s).`
      : `¿Estás seguro de eliminar "${groupName}"?`;
    
    if (window.confirm(confirmMessage)) {
      onDeleteGroup(currentPath);
    }
  };

  const getQuestionDetails = (questionName: string): FormQuestion | undefined => {
    return formQuestions.find(q => q.name === questionName);
  };

  return (
    <AccordionItem
      value={currentPath.join('/')}
      className={`border rounded-lg px-4 ${level > 0 ? 'ml-4' : ''}`}
    >
      <div className="flex items-center justify-between w-full">
        <AccordionTrigger className="hover:no-underline flex-1">
          <div className="flex items-center gap-3">
            {editingGroup ? (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveRename();
                    } else if (e.key === 'Escape') {
                      handleCancelRename();
                    }
                  }}
                  className="flex-1"
                  autoFocus
                />
                <Button size="sm" onClick={handleSaveRename}>
                  Guardar
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelRename}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <>
                <Folder className="text-blue-500" size={18} />
                <span className="font-semibold">{groupName}</span>
                <Badge variant="secondary">
                  {totalQuestions} pregunta{totalQuestions !== 1 ? 's' : ''}
                </Badge>
                {totalSubgroups > 0 && (
                  <Badge variant="outline">
                    {totalSubgroups} subgrupo{totalSubgroups !== 1 ? 's' : ''}
                  </Badge>
                )}
              </>
            )}
          </div>
        </AccordionTrigger>

        {!editingGroup && (
          <div className="flex items-center gap-2 ml-2">
            <Dialog open={isSubgroupDialogOpen} onOpenChange={setIsSubgroupDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  title="Crear subgrupo"
                >
                  <FolderPlus size={14} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Subgrupo en "{groupName}"</DialogTitle>
                  <DialogDescription>
                    Ingresa un nombre para el nuevo subgrupo
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Nombre del subgrupo"
                  value={newSubgroupName}
                  onChange={(e) => setNewSubgroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateSubgroup();
                    }
                  }}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsSubgroupDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateSubgroup}>
                    Crear
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              size="sm"
              variant="ghost"
              onClick={handleStartRename}
              className="h-8 w-8 p-0"
            >
              <Edit2 size={14} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDeleteGroup}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>
      <AccordionContent className="pt-4">
        {/* Questions at this level */}
        {node.questions.length > 0 && (
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-muted-foreground">Preguntas:</p>
            {node.questions.map((questionItem) => {
              // Manejar tanto la estructura antigua (string) como la nueva (objeto)
              const questionName = typeof questionItem === 'string' ? questionItem : questionItem.name;
              const questionKey = typeof questionItem === 'string' ? questionItem : questionItem.key;
              
              const question = getQuestionDetails(questionName);
              return (
                <div
                  key={questionName}
                  className="flex items-start justify-between p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      {questionName}
                    </p>
                    {questionKey && questionKey !== questionName && (
                      <p className="text-xs text-blue-600 mt-1">
                        ID: {questionKey}
                      </p>
                    )}
                    {question && (
                      <>
                        <p className="text-xs text-muted-foreground mt-1">
                          {question.text}
                        </p>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {question.type}
                        </Badge>
                      </>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRemoveQuestion(currentPath, questionName)}
                    className="h-8 w-8 p-0 ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X size={16} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Subgroups */}
        {node.subgroups && Object.keys(node.subgroups).length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-muted-foreground mb-2">Subgrupos:</p>
            <Accordion type="multiple" className="space-y-2">
              {Object.entries(node.subgroups).map(([subgroupName, subgroupNode]) => (
                <GroupNode
                  key={subgroupName}
                  groupName={subgroupName}
                  node={subgroupNode}
                  path={currentPath}
                  formQuestions={formQuestions}
                  onCreateSubgroup={onCreateSubgroup}
                  onRenameGroup={onRenameGroup}
                  onDeleteGroup={onDeleteGroup}
                  onRemoveQuestion={onRemoveQuestion}
                  level={level + 1}
                />
              ))}
            </Accordion>
          </div>
        )}

        {node.questions.length === 0 && (!node.subgroups || Object.keys(node.subgroups).length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Este grupo está vacío
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

export function GroupedQuestions({
  groups,
  formQuestions,
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
  onRemoveQuestion,
}: GroupedQuestionsProps) {
  const [newGroupName, setNewGroupName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const groupNames = Object.keys(groups);

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) {
      toast.error("El nombre del grupo no puede estar vacío");
      return;
    }

    try {
      onCreateGroup([], newGroupName.trim());
      setNewGroupName("");
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al crear grupo");
    }
  };

  const getTotalQuestions = () => {
    return Object.values(groups).reduce((sum, node) => sum + countQuestionsInNode(node), 0);
  };

  return (
    <Card className="h-[calc(100vh-250px)]">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Grupos Configurados</span>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{getTotalQuestions()} preguntas</Badge>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="flex items-center gap-2">
                  <FolderPlus size={16} />
                  Nuevo Grupo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Nuevo Grupo</DialogTitle>
                  <DialogDescription>
                    Ingresa un nombre descriptivo para el grupo de preguntas
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Ej: Habilidades_Relacionales"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateGroup();
                    }
                  }}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateGroup}>
                    Crear
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          {groupNames.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderPlus className="mx-auto mb-4 text-gray-300" size={48} />
              <p>No hay grupos creados</p>
              <p className="text-sm mt-2">Crea tu primer grupo para comenzar</p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {groupNames.map((groupName) => (
                <GroupNode
                  key={groupName}
                  groupName={groupName}
                  node={groups[groupName]}
                  path={[]}
                  formQuestions={formQuestions}
                  onCreateSubgroup={onCreateGroup}
                  onRenameGroup={onRenameGroup}
                  onDeleteGroup={onDeleteGroup}
                  onRemoveQuestion={onRemoveQuestion}
                />
              ))}
            </Accordion>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
